import { useCallback } from "react";
import useMyMontepioContext from "../../../components/Context/useMyMontepioContext";
import { MGButton } from "../../../designSystem/components/MGButton";
import { MGLabel } from "../../../designSystem/components/MGLabel";
import { BackgroundForm } from "../components/BackgroundForm";
import styles from "../Health.module.css";
import { TFormHealth } from "..";
import { MGSubtitle } from "../../../designSystem/components/MGSubtitle";
import { MGCard } from "../../../designSystem/components/MGCard";
import { MGText } from "../../../designSystem/components/MGText";
import { MGOutputItem } from "../../../designSystem/components/MGOutputItem";
import { MGNote } from "../../../designSystem/components/MGNote";
import { paymentFrequencyOptions } from "../QuotasModalityPlanStep/types";
import { moduleOptions } from "../ModuleStep/components/ModuleBottomSheet/types";
import confirmStyles from "./ConfirmStep.module.css";
import { Plus } from "lucide-react";
import { useCreateMutation } from "../api";
import { MGGenericError } from "../../../designSystem/components/MGGenericError";
import { MGLoader } from "../../../designSystem/components/MGLoader";

type TConfirmStepProps = {
  formHealth: TFormHealth;
  onContinue: (formHealth: TFormHealth) => void;
  onBack: VoidFunction;
};

export const ConfirmStep = ({ formHealth, onContinue, onBack }: TConfirmStepProps) => {
  const { isMobileAuth } = useMyMontepioContext();

  const { mutate, isError, isPending } = useCreateMutation(formHealth.moduleType!, {
    onSuccess: () => onDone(),
  });

  const create = useCallback(() => {
    mutate({
      associate: {
        dateOfBirth: formHealth.dateOfBirth!,
        gender: formHealth.gender!,
      },
      healthInsurancePlan: {
        supplementaryHealthPlan: formHealth.supplementaryHealthPlan!,
        moduleType: formHealth.moduleType!,
        paymentFrequency: formHealth.paymentFrequency!,
        supplementaryHealthPaymentFrequency: formHealth.protection?.paymentFrequency,
      },
    });
  }, [
    mutate,
    formHealth.dateOfBirth,
    formHealth.gender,
    formHealth.supplementaryHealthPlan,
    formHealth.moduleType,
    formHealth.paymentFrequency,
    formHealth.protection?.paymentFrequency,
  ]);

  const module = moduleOptions.find((option) => option.value === formHealth.moduleType);

  const onDone = useCallback(() => {
    onContinue({ ...formHealth });
  }, [formHealth, onContinue]);

  if (isPending) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGGenericError
          actionsAtBottom={isMobileAuth}
          title="Mensagem de Erro !"
          description="Explicação adicional, próximos passos e outras opções de contexto"
          primaryActionLabel="Tentar novamente"
          secondaryActionLabel="Voltar"
          onPrimaryAction={create}
          onSecondaryAction={onBack}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <MGLabel style={{ marginTop: 8, marginBottom: 4 }} labelStyleDefinition={"80-03"}>
        {"Subscrição da solução integrada"}
      </MGLabel>

      <MGSubtitle style={{ marginBottom: 16 }} subtitleStyleDefinition={"100-06"}>
        {"Modalidade de Saúde"}
        {formHealth.supplementaryHealthPlan && " + Plano Assegura a Saúde"}
      </MGSubtitle>

      <BackgroundForm style={{ marginBottom: isMobileAuth ? 32 : 0 }}>
        <MGCard style={{ marginBottom: 16 }}>
          <MGText
            textStyleDefinition="body-100-06"
            color="text-secondary-02"
            style={{ marginBottom: 24 }}>
            {"Modalidade de Saúde"}
          </MGText>
          <div style={{ gap: 24, display: "flex", flexDirection: "column" }}>
            <MGOutputItem title="Módulo" text={`${module?.genderLabel} (${module?.name})`} />
            <MGOutputItem
              title="Periodicidade da Quota"
              text={
                paymentFrequencyOptions.find(
                  (option) => option.value === formHealth.paymentFrequency,
                )?.name ?? ""
              }
            />
            <MGOutputItem title="Valor da Quota " text="200,00 €" />
            <MGOutputItem title="Método de pagamento" text="Débito Direto" />
          </div>
        </MGCard>

        <div className={confirmStyles.planConnector} role="separator" aria-label="Mais">
          <span className={confirmStyles.planConnectorIcon} aria-hidden="true">
            <Plus size={16} strokeWidth={3} />
          </span>
        </div>

        {formHealth.supplementaryHealthPlan && (
          <MGCard style={{ marginBottom: 16 }}>
            <MGText
              textStyleDefinition="body-100-06"
              color="text-secondary-02"
              style={{ marginBottom: 24 }}>
              {"Plano Assegura Saúde"}
            </MGText>
            <div style={{ gap: 24, display: "flex", flexDirection: "column" }}>
              <MGOutputItem
                title="Periodicidade da Quota"
                text={
                  paymentFrequencyOptions.find(
                    (option) => option.value === formHealth.protection?.paymentFrequency,
                  )?.name ?? ""
                }
              />
              <MGOutputItem title="Valor da Quota " text="200,00 €" />
              <MGOutputItem title="Método de pagamento" text="Débito Direto" />
            </div>
          </MGCard>
        )}

        <MGNote
          icon="info"
          backgroundColor="transparent"
          description="A subscrição do Plano Assegura a Saúde está sujeita a avaliação clínica pela AdvanceCare. O plano permanecerá em estado pendente até à conclusão do processo de avaliação."
        />
      </BackgroundForm>

      <MGButton
        fullWidth={isMobileAuth}
        className={`${styles.buttonFooter} ${isMobileAuth ? styles.buttonFooterFixed : ""}`}
        onClick={create}>
        {"Confirmar"}
      </MGButton>
    </div>
  );
};
